export const en = {
  appName: "Client Portal",
  appSubtitle: "Order & Document Portal",
  language: {
    label: "Language",
    en: "English",
    zhCN: "简体中文",
  },
  shell: {
    navOperations: "Operations",
    navInbox: "Inbox",
    navOrg: "Organization",
    searchPlaceholder: "Search orders, documents…",
    searchAria: "Open search",
    toggleSidebar: "Toggle sidebar",
    themeLight: "Switch to light mode",
    themeDark: "Switch to dark mode",
    profileMenu: "Profile menu",
    clientAdminRole: "Client Administrator",
    clientStaffRole: "Client Staff",
    storeUserRole: "Store User",
  },
  statuses: {
    order: {
      DRAFT: "Draft",
      SUBMITTED: "Submitted",
      PROCESSING: "Processing",
      WAITING_CLIENT: "Waiting on client",
      WAITING_LOGISTICS: "Waiting on logistics",
      ON_HOLD: "On hold",
      COMPLETED: "Completed",
      CLOSED: "Closed",
      CANCELLED: "Cancelled",
      REJECTED: "Rejected",
    },
    step: {
      PENDING: "Pending",
      NOT_STARTED: "Not started",
      ASSIGNED: "Assigned",
      IN_PROGRESS: "In progress",
      COMPLETED: "Completed",
      SKIPPED: "Skipped",
      REJECTED: "Rejected",
      WAITING: "Waiting",
      WAITING_CLIENT: "Waiting on client",
      WAITING_LOGISTICS: "Waiting on logistics",
      WAITING_DOCUMENT: "Waiting on document",
      PENDING_APPROVAL: "Pending approval",
      REOPENED: "Reopened",
      CANCELLED: "Cancelled",
    },
    approval: {
      PENDING_APPROVAL: "Pending approval",
      APPROVED: "Approved",
      REJECTED: "Rejected",
      REVISION_REQUESTED: "Revision requested",
      RESUBMITTED: "Resubmitted",
      CANCELLED: "Cancelled",
    },
    sla: {
      OK: "On track",
      WARNING: "Warning",
      APPROACHING: "Approaching",
      BREACHED: "Breached",
      OVERDUE: "Overdue",
      ESCALATED: "Escalated",
    },
    ticket: {
      OPEN: "Open",
      PENDING: "Pending",
      WAITING: "Waiting",
      RESOLVED: "Resolved",
      CLOSED: "Closed",
    },
  },
  validation: {
    required: "This field is required.",
    invalidEmail: "Invalid email address.",
    minLength: "Must be at least {min} characters.",
    networkError: "Network error. Please try again.",
    sessionExpired: "Session expired. Please sign in again.",
    somethingWrong: "Something went wrong.",
    noPermission: "You do not have permission to perform this action.",
  },
  common: {
    save: "Save",
    cancel: "Cancel",
    create: "Create",
    search: "Search",
    retry: "Retry",
    loading: "Loading…",
    pageNotFound: "Page Not Found",
    pageNotFoundHint: "The page you requested does not exist.",
    goHome: "Go home",
    yes: "Yes",
    no: "No",
    none: "None",
    next: "Next",
    back: "Back",
    prevPage: "Previous",
    nextPage: "Next",
    rowsPerPage: "Rows per page",
    submit: "Submit",
    remove: "Remove",
    add: "Add",
    copy: "Copy",
    copied: "Copied",
    actions: "Actions",
    open: "Open",
    navGroup: "Navigation",
    pageSummary: "Page {page} / {totalPages} · {total} total",
    noData: "No data available",
    selectedCount: "{n} selected",
    searchPlaceholder: "Search…",
    confirm: "Confirm",
    loadFailed: "Failed to load",
    retryLater: "Please try again later",
    contactAdminLimit: "Limit reached. Please contact an administrator.",
    device: "Device",
    lastActive: "Last active",
    current: "Current",
    other: "Other",
    noSessions: "No sessions",
    invitedAt: "Invited at",
    noInvitations: "No invitations",
    actor: "Actor",
    resource: "Resource",
    time: "Time",
    provider: "Provider",
    model: "Model",
    openMenu: "Open menu",
    closeMenu: "Close menu",
    close: "Close",
    goTo: "Go to…",
    noCommands: "No matching commands",
  },
  login: {
    title: "Sign in",
    email: "Email",
    password: "Password",
    submit: "Sign in",
    failed: "Sign-in failed. Check your email and password.",
    unreachable: "Cannot reach the API. Confirm it is running.",
    forgotPassword: "Forgot password?",
    twoFactorTitle: "Two-factor authentication",
    twoFactorHint: "Enter the code from your authenticator app or email",
    twoFactorCode: "Verification code",
    twoFactorSubmit: "Verify and sign in",
    twoFactorBack: "Back",
    twoFactorFailed: "Invalid or expired verification code",
  },
  forgotPassword: {
    title: "Reset password",
    hint: "Enter your sign-in email and we will send a reset link.",
    submit: "Send reset email",
    sent: "Email sent",
    back: "Back to sign in",
  },
  resetPassword: {
    title: "Set new password",
    newPassword: "New password",
    confirmPassword: "Confirm password",
    submit: "Update password",
    ok: "Password updated. Please sign in",
    fail: "Reset failed",
    missingToken: "Missing reset token. Open the link from your email again",
    mismatch: "Passwords do not match",
  },
  acceptInvite: {
    title: "Accept Invitation",
    body: "Set your password to join {name}.",
    organization: "Organization",
    expired: "This invitation has expired",
    invalid: "This invitation is invalid",
    fullName: "Full name (optional)",
    password: "Password",
    confirmPassword: "Confirm password",
    submit: "Join Organization",
    ok: "Account ready. You can sign in.",
    fail: "Invitation is invalid or expired",
    mismatch: "Passwords do not match",
    passwordMin: "Password must be at least 10 characters",
    missingToken: "Invitation link is missing or invalid",
    goLogin: "Go to sign in",
  },
  twoFactorSetup: {
    title: "Set up two-factor authentication",
    body: "Scan the secret with an authenticator app, then enable.",
    enable: "Enable 2FA",
    disable: "Disable 2FA",
    enabled: "Enabled",
    disabled: "Not enabled",
    scanHint: "Scan the QR code or enter the secret manually",
    manualSecret: "Manual secret",
    confirmCode: "Verification code",
    confirm: "Confirm and enable",
    passwordConfirm: "Enter your sign-in password before disabling",
  },
  nav: {
    home: "Dashboard",
    orderServices: "Order Services",
    tickets: "Tickets",
    approvals: "Approvals",
    announcements: "Announcements",
    members: "Users",
    billing: "Billing Accounts",
    masterDocuments: "Master Documents",
    endCustomers: "End Customers",
    stores: "Branches",
    teams: "Teams",
    settings: "Settings",
  },
  clientTeams: {
    title: "Teams",
    description:
      "Client Organization Teams for Order Service Access (not staff RBAC).",
    empty: "No teams yet",
    create: "New team",
    created: "Team created",
    createFail: "Failed to create team",
    formName: "Name",
    formDescription: "Description",
    members: "Members",
    addMember: "Add member",
    endCustomers: "End Customer coverage",
    addEndCustomer: "Add End Customer",
    remove: "Delete team",
    removeConfirm:
      "Delete this team? Access grants to the team remain until removed on each order.",
  },
  dashboard: {
    title: "Client Dashboard",
    description:
      "A customer overview of orders in processing, items waiting on you, and the latest updates for {org}.",
    orgFallback: "your organization",
    createOrder: "+ New Order Service",
    ordersProcessing: "Orders Processing",
    ordersProcessingHint: "Active Order Services currently with logistics.",
    processingEmpty: "No Orders Processing",
    processingEmptyDesc: "New Order Services will appear here.",
    waitingMyAction: "Waiting My Action",
    waitingMyActionHint: "Uploads, approvals, and decisions waiting on you.",
    decisionNeeded: "Decision Needed: {title}",
    waitingEmpty: "Nothing Waiting On You",
    waitingEmptyDesc: "You're all caught up.",
    recentUpdates: "Recent Updates",
    recentUpdatesHint: "Latest activity across your organization.",
    updatesEmpty: "No Recent Activity",
    updatesEmptyDesc: "Updates will appear here as work progresses.",
    stepsProgress: "{done} of {total} steps",
    dueNotStarted: "Not started",
    dueSoon: "Due soon",
    dueOverdue: "Overdue",
    expiryBannerTitle: "Master Document expiring soon",
    expiryBannerBody:
      "{items}. Re-upload from the Master Document Library so Order Services keep a valid copy.",
    expiryItem: "{name} expires {date} ({days})",
    daysRemaining: "{n} days remaining",
    daysExpired: "expired {n} days ago",
    reviewLibrary: "Review library",
    // legacy keys kept for other references
    metricInProgress: "In progress",
    metricWaiting: "Waiting",
    metricCompleted: "Completed",
    metricOverdue: "Overdue",
    recentUploads: "Recent uploads",
    recentEmpty: "No uploads yet",
    recentEmptyDesc: "Uploaded files will appear here",
    viewOrders: "View orders",
    uploadedAt: "Uploaded",
  },
  orderServices: {
    title: "Order Services",
    search: "Search by number, service type, shipment reference, booking number, or container number",
    create: "New order",
    empty: "No order services",
    emptyDesc: "Create your first Order Service to collaborate",
    filterAll: "All",
    filterInProgress: "In progress",
    filterWaiting: "Waiting",
    filterOverdue: "Overdue",
    filterCompleted: "Completed",
    colNumber: "Number",
    colService: "Service type",
    colStore: "Branch",
    colStoreAgent: "End Customer / Branch",
    colStatus: "Status",
    colStep: "Current step",
    colProgress: "Progress",
    overdue: "Overdue",
    clientAction: "Action required",
    backToList: "Back to list",
    unassigned: "Unassigned",
    filterEndCustomer: "End Customer",
    colEndCustomer: "End Customer",
  },
  orderServiceDetail: {
    title: "Order Details",
    overview: "Overview",
    documents: "Documents",
    documentsTable: "Required & Uploaded Documents",
    documentsColName: "Document",
    documentsColCategory: "Category",
    documentsColUploader: "Uploaded By",
    documentsColStatus: "Status",
    documentsColActions: "Actions",
    documentsCategoryRequired: "Required",
    documentsCategoryOptional: "Optional",
    documentsCategoryExtra: "Extra",
    documentsCategoryRemarks: "Remarks only",
    documentsStep: "Step: {name}",
    documentsNoStep: "No workflow step",
    documentsNotUploaded: "Not uploaded",
    documentsSourceMaster: "Master Document",
    documentsSourceOrder: "Order Upload",
    atAGlance: "At a glance",
    serviceType: "Service Type",
    store: "Branch",
    status: "Status",
    activeStep: "Current Step",
    missingDocs: "Documents to upload",
    completedWork: "Completed steps",
    overdue: "Overdue",
    clientActionNeeded: "Your action needed",
    workflow: "Workflow",
    workflowTitle: "Workflow",
    workflowSubtitle: "{template} · {count} steps · click a step for details",
    workflowTemplate: "Template: {name}",
    centerCurrent: "Jump to current step",
    timelineSlaTarget: "target",
    timelineCompleted: "Completed",
    timelineInProgress: "In progress",
    timelineApprovalRequired: "Approval required",
    timelineOverdueHint: "Overdue · escalate if still blocked",
    timelineScrollLeft: "Scroll left",
    timelineScrollRight: "Scroll right",
    history: "Audit Trail",
    activityEmpty: "No activity yet",
    auditColTimestamp: "Timestamp",
    auditColUser: "User",
    auditColAction: "Action",
    auditColResource: "Resource",
    auditColIp: "IP",
    branch: "Logistics Branch",
    priority: "Priority",
    references: "References",
    summaryClient: "Client",
    summaryStore: "Branch",
    summaryServiceType: "Service Type",
    summaryStatus: "Status",
    summaryBranch: "Branch",
    summaryOwner: "Owner",
    summaryActiveStep: "Active Step",
    summaryResponsible: "Responsible",
    unassigned: "Unassigned",
    progressSteps: "{done} / {total} required steps completed",
    progressDocs: "{done}/{total} mandatory documents",
    progressRef: "Ref {ref}",
    noActiveStep: "No active step",
    noMissing: "No missing documents",
    noCompleted: "No completed steps yet",
    noHistory: "No history yet",
    noNextAction: "No next action",
    noAlerts: "No active alerts",
    exceptionsAlerts: "Exceptions & Alerts",
    exceptionCount: "{count} Exceptions",
    comments: "Comments",
    access: "Access",
    accessBypass: "Client Admin has full access within this organization.",
    accessInheritedStaff: "Inherited via Team responsibility",
    accessInheritedClient: "Inherited via Client Team End Customer coverage",
    accessGrants: "People and teams with access",
    accessDerived: "Via workflow assignment",
    accessDerivedHint: "Assignment is not the same as an Access grant.",
    accessAddUser: "User",
    accessAddTeam: "Team",
    accessGrant: "Grant access",
    accessPrincipalUser: "User",
    accessPrincipalTeam: "Team",
    accessSourceCreator: "Creator",
    accessSourceExplicit: "Granted",
    commentsEmpty: "No comments yet",
    commentTimeline: "Timeline",
    commentAdd: "Add Comment",
    commentCommented: "commented",
    commentRequestedDocument: "requested a document",
    commentRequestedClose: "requested closure",
    commentPlaceholder: "Write a comment…",
    commentSend: "Send",
    commentOk: "Comment sent",
    commentFail: "Failed to send comment",
    mandatory: "Required",
    optional: "Optional",
    progress: "Progress",
    upload: "Upload document",
    uploadTitle: "Upload Document",
    uploadBrowse: "Drop a file or click to browse",
    uploadFormats: "PDF, XLS, XLSX, DOC, DOCX, JPG, PNG · Up to 5 MB",
    takePhoto: "Take photo",
    chooseFile: "Choose file",
    uploading: "Uploading…",
    uploadOk: "Upload successful",
    uploadFail: "Upload failed. Please try again",
    checklist: "Document checklist",
    checklistEmpty: "No checklist items",
    checklistAddExtra: "Add Extra document",
    checklistDocType: "Document type",
    checklistAddOk: "Checklist item added",
    checklistAddFail: "Failed to add",
    remarksSave: "Save remarks",
    remarksOk: "Remarks saved",
    remarksFail: "Failed to save remarks",
    remarksPlaceholder: "Enter remarks…",
    linkMaster: "Link master document",
    linkMasterPick: "Select master document",
    linkMasterOk: "Master document linked",
    linkMasterFail: "Failed to link",
    linkMasterEmpty: "No master documents yet — upload in Master documents first",
    masterDocs: "Master Documents",
    masterDocsEmpty: "No master documents yet",
    actionEdit: "Edit fields",
    editOk: "Saved",
    editFail: "Failed to save",
    download: "Download",
    preview: "Preview",
    deleteDoc: "Delete document",
    deleteDocOk: "Deleted",
    deleteDocFail: "Failed to delete",
    recentUploads: "Recent uploads",
    commentDelete: "Delete",
    commentDeleteOk: "Comment deleted",
    commentDeleteFail: "Failed to delete",
    stepStatus: "Status",
    stepAssignedTo: "Assigned To",
    stepDurationSla: "Duration & SLA",
    stepElapsed: "Elapsed",
    stepOverdue: "Overdue",
    stepApproval: "Approval",
    stepApprovalNotRequired: "No approval required for this step.",
    stepClientAction: "Client Action",
    stepClientActionRequired: "This step needs your action.",
    stepClientActionNotRequired: "No client action required for this step.",
    stepExpectedType: "Expected Type",
    stepVerified: "Verified",
    stepUploaded: "Uploaded",
    stepNotUploaded: "Not Uploaded",
    stepDrawerApproval: "Approval Required",
    stepDrawerParallel: "Parallel Group",
    stepDrawerSla: "SLA target",
    stepSlaWarning: "SLA Warning",
    stepStartedAt: "Started At",
    stepCompletedAt: "Completed At",
    stepDrawerMinutes: "min",
    stepDrawerChecklist: "Step Documents",
    stepDrawerChecklistEmpty: "No documents linked to this step yet",
    stepDrawerDocumentsLoading: "Loading step documents…",
    stepDrawerDocumentsError: "Could not load step documents",
    stepDrawerStepOf: "Step {step} of {total}",
    stepDrawerComments: "Step Comments",
    stepDrawerCommentsEmpty: "No comments on this step",
    stepCommentsError: "Could not load step comments",
    stepCommentPlaceholder: "Add a comment about this step…",
    lastUpdated: "Last Updated",
    shipmentDetails: "Shipment Details",
    shipmentVessel: "Vessel / Voyage",
    shipmentVoyage: "Voyage",
    shipmentPol: "Port Of Loading (POL)",
    shipmentPod: "Port Of Discharge (POD)",
    shipmentRoute: "POL → POD",
    shipmentEta: "ETA / ETD",
    shipmentContainers: "Container Number",
    containerNumbersTitle: "Container Numbers",
    containerOpenCount: "View {count} numbers",
    containerPastePlaceholder:
      "Paste from Excel, one number per line or separated by commas.",
    containerValidCount: "{count} valid",
    containerInvalid:
      "Not an 11-character container number (4 letters and 7 digits).",
    containerTooMany: "At most 200 container numbers.",
    containerFilter: "Find a container number",
    containerNoMatch: "No matching container number",
    containerRemove: "Remove {number}",
    shipmentCargo: "Cargo / Volume",
    logisticsBranch: "Logistics Branch",
    billingReference: "Billing Reference",
    bookingNumber: "Booking Number / BL Number",
    notProvided: "Not Provided",
  },
  masterDocuments: {
    title: "Master Documents",
    description:
      "Reusable master documents for this organization; order checklists can link them directly.",
    endCustomer: "End Customer",
    pickEndCustomer: "Select an End Customer",
    pickEndCustomerDesc:
      "Agent organizations keep a separate master library per End Customer.",
    empty: "No master documents",
    emptyDesc: "Upload the first master document",
    upload: "Upload master document",
    colName: "File name",
    colType: "Document type",
    colVersion: "Version",
    colExpiry: "Expiry",
    docType: "Document type",
    issueDate: "Issue date (optional)",
    expiryDate: "Expiry date (optional)",
    uploadOk: "Upload successful",
    uploadFail: "Upload failed",
    download: "Download",
    preview: "Preview",
    noExpiry: "No expiry",
    newVersion: "New Version",
    newVersionTitle: "Upload New Version",
    newVersionHint: "Replaces the current file and increments the version.",
    newVersionOk: "Version uploaded",
    delete: "Delete",
    deleteTitle: "Delete Master Document",
    deleteConfirm:
      "Soft-delete “{name}”? Linked order checklists keep history; this file leaves the library.",
    deleteOk: "Deleted",
    deleteFail: "Failed to delete",
  },
  wizard: {
    title: "Create Order Service",
    stepOf: "Step {current} / {total}",
    steps: {
      SELECT_PARTIES: "Select Parties",
      SELECT_WORKFLOW_TEMPLATE: "Select Workflow Template",
      CUSTOMIZE_WORKFLOW: "Customize Workflow & Documents",
      ENTER_ORDER_INFO: "Enter Order Details",
      REVIEW_AND_SUBMIT: "Review And Submit",
    },
    endCustomer: "End Customer",
    endCustomerRequired: "Please select an End Customer",
    endCustomerSearch: "Search End Customers…",
    workflowTemplate: "Workflow Template",
    workflowTemplateRequired: "Please select a Workflow Template",
    workflowTemplateSearch: "Search Workflow Templates…",
    workflowTemplateHint: "Service Type is linked to the selected template.",
    logisticsBranch: "Logistics Branch",
    branchLockedHint: "Uses your assigned Logistics Branch.",
    customerReference: "Customer Reference",
    shipmentReference: "Shipment Reference",
    priority: "Priority",
    remarks: "Remarks",
    store: "Branch (Optional)",
    storeSearch: "Search branches…",
    storeNone: "No Branch",
    storeSkip: "Continue Without Branch",
    customizeHint:
      "Reorder steps, edit step properties, add or remove optional steps and checklist items. Link master documents when needed.",
    addStep: "Add Step",
    addStepName: "Step Name",
    removeStep: "Remove Step",
    addChecklistItem: "Add Checklist Item",
    removeChecklistItem: "Remove",
    documentType: "Document Type",
    documentTypeSearch: "Search document types…",
    mandatory: "Mandatory",
    optional: "Optional",
    masterDocuments: "Master Documents",
    masterDocumentsHint:
      "Select a master document from this Client Organization to link onto the Order Service checklist.",
    masterPick: "Select Master Document",
    masterSearch: "Search master documents…",
    masterEmpty: "No master documents for this organization",
    masterLinkToChecklist: "Link To Checklist Item (Optional)",
    masterLink: "Link",
    masterLinked: "Linked",
    masterLinkOk: "Master document linked",
    checklistFromStepHint:
      "Documents required by workflow steps cannot be removed here. Only Extra checklist items can be removed.",
    checklistFromStep: "From Step",
    checklistExtra: "Extra",
    uploadHint: "Choose a file to upload.",
    dragToReorder: "Drag to reorder",
    moveUp: "Move up",
    moveDown: "Move down",
    stepCount: "{n} steps",
    stepProperties: "Step Properties",
    stepPropertiesDesc: "Edit rules for this workflow step on the draft Order Service.",
    stepPropertiesOk: "Step properties saved",
    stepSla: "SLA (minutes)",
    stepSlaWarning: "SLA Warning (Minutes)",
    stepRequired: "Required Step",
    stepApprovalRequired: "Approval Required",
    stepClientAction: "Client Action Required",
    stepCanSkip: "Can skip",
    stepCanReopen: "Can reopen",
    stepCanRepeat: "Can repeat",
    stepDocumentRequirements: "Document Type Requirements",
    save: "Save",
    reviewTitle: "Review Before Submit",
    reviewSteps: "Workflow Steps",
    reviewStepsAndChecklist: "Workflow Steps & Document Checklist",
    reviewExtraDocuments: "Extra Document",
    reviewChecklist: "Document Checklist",
    reviewMissing: "{count} Missing Mandatory Documents",
    reviewServiceType: "Service Type",
    reviewNumber: "Number",
    submit: "Submit Order",
    next: "Next",
    back: "Back",
    submitOk: "Submitted",
    submitting: "Submitting…",
    createFail: "Failed to create. Please try again",
    applyFail:
      "Order Service was saved as Draft, but some customizations could not be applied. Please finish on the detail page",
    templateLoadFail: "Failed to load Workflow Template. Please try again",
    updateFail: "Failed to save order details. Please try again",
    submitFail: "Failed to submit. Please try again",
    customizeFail: "Failed to update workflow. Please try again",
    priorityLow: "Low",
    priorityNormal: "Normal",
    priorityHigh: "High",
    priorityUrgent: "Urgent",
    none: "—",
  },
  members: {
    title: "Users",
    description:
      "Client Admin can manage Client Staff and Branch Users in this organization.",
    search: "Search name or email",
    add: "Invite Member",
    create: "Create User",
    createTitle: "Create User",
    createName: "Name",
    createPassword: "Password",
    createPasswordHint: "At least 10 characters. They can sign in with this password.",
    createOk: "User created",
    createFail: "Failed to create user",
    empty: "No members yet",
    emptyDesc: "Invite Client Staff to collaborate on orders",
    colName: "Name",
    colEmail: "Email",
    colRole: "Role",
    colEndCustomers: "End Customers",
    colEndCustomerBranch: "End Customer / Branch",
    colBranch: "Branch",
    colCreated: "Joined",
    roleAdmin: "Client Admin",
    roleStaff: "Client Staff",
    roleStoreUser: "Branch User",
    userId: "User ID",
    userIdHint:
      "Invite sends an email; the invitee sets a password and joins this organization",
    inviteEmail: "Email",
    inviteHint:
      "We email an invite link. The invitee sets a password and joins this organization.",
    inviteOk: "Invitation sent",
    inviteFail: "Failed to send invitation",
    invitationsTitle: "Invitations",
    invitationsDesc: "People who were invited and have not joined yet.",
    colStatus: "Status",
    colExpires: "Expires",
    statusPending: "Pending",
    statusExpired: "Expired",
    statusAccepted: "Accepted",
    emptyInvitations: "No open invitations",
    searchUser: "Search Client user",
    searchUserHint: "Type at least 2 characters of name or email",
    searchUserEmpty: "No matching Client users",
    pickUser: "Select user",
    userIdRequired: "User ID is required",
    role: "Role",
    assignEndCustomers: "Assigned End Customers",
    assignEndCustomersHint: "Required for Client Staff on Agent organizations",
    assignStores: "Assigned Branches",
    assignStoresHint: "Branches are grouped by End Customer.",
    assignStoresUngrouped: "Other Branches",
    addOk: "Added",
    addFail: "Failed to add",
    changeRole: "Change role",
    roleUpdated: "Role updated",
    roleUpdateFail: "Failed to update role",
    removeConfirm: "Remove this member?",
    removeOk: "Removed",
    removeFail: "Failed to remove",
    forbidden: "Only Client Admin can manage members",
  },
  tickets: {
    title: "Tickets",
    search: "Search subject",
    create: "New ticket",
    empty: "No tickets yet",
    emptyDesc: "After you submit, the logistics team will follow up",
    colSubject: "Subject",
    colStatus: "Status",
    colPriority: "Priority",
    colUpdated: "Updated",
    formSubject: "Subject",
    formBody: "Details",
    formPriority: "Priority",
    priorityNormal: "Normal",
    priorityHigh: "High",
    priorityLow: "Low",
    reply: "Reply",
    replyPlaceholder: "Write a reply",
    createOk: "Created",
    createFail: "Failed to create",
    replyOk: "Sent",
    replyFail: "Failed to send",
    back: "Back to list",
  },
  approvals: {
    title: "Approvals",
    description: "Approvals waiting on you or needing resubmission.",
    empty: "No approvals",
    emptyDesc: "Items appear here when you are an approver or revision is needed",
    colOrder: "Order",
    colStatus: "Status",
    colAttempt: "Attempts",
    colUpdated: "Updated",
    approve: "Approve",
    reject: "Reject",
    rejectReason: "Rejection reason",
    approved: "Approved",
    rejected: "Rejected",
    resubmit: "Resubmit",
    resubmitOk: "Resubmitted",
    actionFail: "Action failed",
  },
  clientConsent: {
    title: "Client Decision",
    description:
      "Agree or reject one action, such as a permit. This does not complete the step.",
    empty: "No Client Decisions",
    emptyDesc: "Requests that need your agreement appear here.",
    colOrder: "Order",
    colTitle: "Action",
    colDays: "Business Days",
    colStatus: "Status",
    statusPending: "Awaiting Your Decision",
    statusAgreed: "Agreed",
    statusRejected: "Rejected",
    statusWithdrawn: "Withdrawn",
    days: "About {n} business days",
    penalty: "Penalty Terms",
    summary: "What Will Be Done",
    decideHint: "Tick the box, then Agree. To decline, choose Reject and write a reason.",
    acknowledge: "Any penalty described above is payable by our organization.",
    agree: "Agree",
    reject: "Reject",
    rejectReason: "Rejection Reason",
    saved: "Saved",
    fail: "Could not save",
    open: "Review",
    viewOnly: "A Client Administrator or Client Staff agrees or rejects this action.",
  },
  billing: {
    title: "Billing Accounts",
    description: "Billing reference accounts for this organization (read-only).",
    empty: "No billing accounts",
    emptyDesc: "Configured by the logistics company admin",
    colName: "Name",
    colScope: "Scope",
    colRef: "External reference",
    colStatus: "Status",
    scopeOrg: "Organization",
    scopeStore: "Branch",
    active: "Active",
    inactive: "Inactive",
    forbidden: "Only Client Admin can view",
  },
  announcements: {
    title: "Announcements",
    empty: "No announcements",
    emptyDesc: "Announcements from the logistics company will appear here",
    markRead: "Mark as read",
    unread: "Unread",
    read: "Read",
  },
  settings: {
    title: "Settings",
    description:
      "Personal profile, organization details, security, sessions, and notification preferences.",
    personal: "Personal",
    org: "Organization",
    organization: "Basic Info",
    orgName: "Organization Name",
    orgForbidden: "Only Client Admin can update organization details.",
    basicInformation: "Basic Information",
    formEngagement: "Engagement",
    engagementDirect: "Direct Client",
    engagementAgent: "Agent",
    corporateIdentity: "Corporate Identity",
    legalName: "Company Name",
    tradeName: "Contact Person",
    registrationNumber: "Registration Number",
    entityType: "Designation",
    selectEntityType: "Select Entity Type",
    entityPrivateLimited: "Private Limited (Sdn. Bhd.)",
    entityPublicLimited: "Public Limited (Bhd.)",
    entityPartnership: "Partnership",
    entitySoleProprietorship: "Sole Proprietorship",
    entityLimitedLiabilityPartnership: "Limited Liability Partnership",
    selectCountry: "Select Country",
    registeredAddress: "Registered Address",
    operationalAddress: "Operational Address",
    businessEmail: "Email",
    supportPhone: "Phone Number",
    addressLine1: "Address Line 1",
    addressLine2: "Address Line 2 (Optional)",
    postalCode: "Zip / Postal Code",
    city: "City",
    state: "State",
    country: "Country",
    profile: "Profile",
    password: "Password",
    security: "Two-Factor Authentication",
    sessions: "Active Sessions",
    notifications: "Notification Preferences",
    name: "Name",
    locale: "Language",
    timezone: "Timezone",
    currentPassword: "Current password",
    newPassword: "New password",
    channelInApp: "In-app",
    channelEmail: "Email",
    channelSms: "SMS",
    channelPush: "Push",
    typeCol: "Notification type",
    transactionalKeepOne:
      "This notification is required — keep at least one channel on.",
    revoke: "Revoke",
    saved: "Saved",
    passwordUpdated: "Password updated",
    requestFailed: "Request failed",
    device: "Device",
    currentSession: "Current",
    otherSession: "Other",
    noSessions: "No sessions",
  },
  notifications: {
    title: "Notifications",
    empty: "No notifications",
    markRead: "Mark as read",
    ariaLabel: "Notifications",
  },
  stores: {
    title: "Branches",
    description: "Manage branches for this organization.",
    empty: "No branches yet",
    emptyDesc: "Create a branch to assign orders and Branch Users",
    create: "New Branch",
    edit: "Edit",
    formName: "Name",
    formCode: "Code (optional)",
    formAddress: "Address (optional)",
    formEndCustomer: "End Customer",
    created: "Created",
    createFail: "Failed to create",
    save: "Save",
    saved: "Saved",
    saveFail: "Failed to save",
    deactivate: "Deactivate",
    activate: "Activate",
    colName: "Branch",
    colCode: "Code",
    colEndCustomer: "End Customer",
    colStatus: "Status",
    statusActive: "Active",
    statusInactive: "Inactive",
    forbidden: "Only Client Admin can manage branches",
  },
  endCustomers: {
    title: "End Customers",
    description: "Manage End Customers (e.g. BYD, Proton) under this Agent organization",
    empty: "No End Customers yet",
    emptyDesc: "Add End Customers to organize branches and orders",
    create: "New End Customer",
    edit: "Edit",
    formName: "Name",
    formCode: "Code (optional)",
    created: "Created",
    createFail: "Failed to create",
    save: "Save",
    saved: "Saved",
    saveFail: "Failed to save",
    remove: "Delete",
    removeOk: "Deleted",
    removeFail: "Failed to delete",
    removeConfirm: "Delete this End Customer?",
    colName: "Name",
    colCode: "Code",
    colStores: "Branches",
    colStatus: "Status",
    statusActive: "Active",
    statusInactive: "Inactive",
    deactivate: "Deactivate",
    activate: "Activate",
    forbidden: "Only Client Admin on Agent organizations can manage End Customers",
  },
  logout: "Sign out",
} as const;

export type LocaleDict = typeof en;
